import { last } from "lodash";
import {
  createTempChatRoomMessage,
  isLastMessageSame,
} from "@/react/components/Chat/ChatWindowV2/helpers";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import type { ChatThreadsSliceState } from "../../createThreadsSlice";
import { MAX_MESSAGES_THRESHOLD, MESSAGES_THRESHOLD } from "../constants";
import type {
  ChatRoomActionsSliceState,
  ChatRoomMessageActionsSliceState,
  ChatRoomsSliceState,
  CreateMessageParams,
} from "../types";

interface MessageDataActionsProps {
  appendLastMessage: (uuid: string, message: ChatRoomMessage) => void;
  clearSpotlightedMessages: (uuid: string) => void;
  deleteMessage: (uuid: string, messageId: number) => void;
  sendRichTextMessage: (
    uuid: string,
    richTextBody: any,
    localAttachments: any[],
    localSgidToObjectMap: any,
    parentMessageId?: number,
  ) => Promise<void>;
  setChatRoomMessages: (uuid: string, messages: ChatRoomMessage[]) => void;
  updateMessage: (uuid: string, message: ChatRoomMessage) => void;
}

export const messageDataActions = (
  set: (fn: (state: ChatRoomsSliceState) => void) => void,
  get: () => ChatRoomsSliceState &
    ChatRoomActionsSliceState &
    ChatRoomMessageActionsSliceState &
    ChatThreadsSliceState,
): MessageDataActionsProps => ({
  setChatRoomMessages: (uuid, messages) =>
    set((state: ChatRoomsSliceState) => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.chatRoomMessages = messages;
      }
    }),
  appendLastMessage: (uuid: string, message: ChatRoomMessage) => {
    const uiState = get().chatRoomStateMap.get(uuid)?.uiState;
    const isInitialPageLoaded = !!uiState?.isInitialPageLoaded;
    const hasNextPage = !!uiState?.hasNextPage;
    if (!isInitialPageLoaded || hasNextPage) {
      return;
    }
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (!chatRoomState) {
        return;
      }
      let messages = chatRoomState?.chatRoomMessages ?? [];
      const hasMessageThresholdReached =
        messages.length > MAX_MESSAGES_THRESHOLD;
      if (hasMessageThresholdReached) {
        messages = messages.slice(-MESSAGES_THRESHOLD);
        chatRoomState.uiState.firstMessageId = messages[0].id;
        chatRoomState.uiState.hasPrevPage = true;
        chatRoomState.chatRoomMessages = messages;
      }
      const lastMessageInArr = last(messages);
      const isNewMessageReached = lastMessageInArr?.id != message.id;
      if (
        isNewMessageReached &&
        !isLastMessageSame(lastMessageInArr, message)
      ) {
        const indexOfMessageWithSameUUID = messages.findIndex(
          (m: ChatRoomMessage) => m.creation_uuid === message.creation_uuid,
        );
        if (indexOfMessageWithSameUUID > -1) {
          messages[indexOfMessageWithSameUUID] = message;
        } else {
          messages.push(message);
        }
        chatRoomState.chatRoomMessages = messages;
      }
    });
  },
  updateMessage: (uuid: string, message: ChatRoomMessage) =>
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        const indexOfMessage = chatRoomState.chatRoomMessages.findIndex(
          (currentMessage: ChatRoomMessage) => currentMessage.id === message.id,
        );
        if (indexOfMessage > -1) {
          chatRoomState.chatRoomMessages[indexOfMessage] = {
            ...chatRoomState.chatRoomMessages[indexOfMessage],
            ...message,
          };
        }
      }
    }),
  deleteMessage: (uuid: string, messageId: number) =>
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.chatRoomMessages = chatRoomState.chatRoomMessages.filter(
          (currentMessage: ChatRoomMessage) => currentMessage.id !== messageId,
        );
      }
    }),
  clearSpotlightedMessages: uuid =>
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.chatRoomMessages = chatRoomState.chatRoomMessages.map(
          (message: ChatRoomMessage) => ({
            ...message,
            spotlighted: false,
          }),
        );
      }
    }),
  sendRichTextMessage: async (
    uuid,
    richTextBody,
    localAttachments,
    localSgidToObjectMap,
    parentMessageId,
  ) => {
    await get().actions.ensureChatRoomParticipant(uuid);
    const chatRoom = get().chatRoomMap.get(uuid);
    const currentParticipant = chatRoom?.current_participant;
    const currentParticipantId = currentParticipant?.id;
    if (!chatRoom || !currentParticipant || !currentParticipantId) {
      return;
    }
    const createMessageParams: CreateMessageParams = {
      chat_room_participant_id: currentParticipantId,
      rich_text_body: richTextBody,
    };
    if (parentMessageId) {
      createMessageParams.parent_message_id = parentMessageId;
    }
    const res = await reactQueryPost(
      internalApi.chatRoomMessages.create({ uuid }),
      {
        chat_room_message: createMessageParams,
      },
    );
    const tempMessage = createTempChatRoomMessage({
      chatRoom,
      richTextBody,
      localAttachments,
      localSgidToObjectMap,
      parentMessageId,
      creationUuid: res.creation_uuid,
      sentAt: res.sent_at,
    });
    if (!tempMessage) {
      return;
    }
    if (parentMessageId) {
      get().threads.appendLastMessage(tempMessage);
    } else {
      get().messageActions.appendLastMessage(uuid, tempMessage);
    }
  },
});
